import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { I, GoogleIcon, MsIcon } from '../icons.jsx';
import { TextField, Checkbox, Footer, AuthDecor, VividWordmark, FormAlert } from '../primitives.jsx';
import { api, errorMessage } from '../api.js';
import { storage, captureRoutingFromUrl, readNoticeFromSearch } from '../storage.js';
import { handoffRedirect, continueSession } from '../sso.js';
import { LangSwitch, useLang } from '../i18n.jsx';

// Why a user landed here again (AUTH-507). The URL's ``reason`` selects a
// key of this map; an unknown value shows nothing, and the value itself is
// never rendered.
// A reason maps to a catalogue key; the sentences live in src/i18n.jsx so the
// Arabic catalogue carries them too (i18n.coverage.spec.js checks both).
const REASONS = {
  expired: 'login.notice.expired',
  'signed-out': 'login.notice.signedOut',
  forbidden: 'login.notice.forbidden',
};

// "erp.example.com/sales/orders" from a next URL, without its query.
function returnLabelFor(next) {
  if (!next) return '';
  try {
    const u = new URL(next, window.location.origin);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch {
    return '';
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { lang, dir, t, setLang } = useLang();
  const { search } = useLocation();
  const notice = readNoticeFromSearch(search);
  // REASONS maps a reason to a catalogue KEY, not to a sentence: LoginPage is in
  // check-i18n-literals' MIGRATED_FILES, so a bare English string here would
  // fail the gate, and an untranslated one would fail i18n.coverage.
  const reasonKey = Object.prototype.hasOwnProperty.call(REASONS, notice.reason)
    ? REASONS[notice.reason]
    : '';
  const returnLabel = returnLabelFor(new URLSearchParams(search).get('next') || '');
  // The address already held in this browser first; the link's is only a
  // fallback, since a link can be seen by others.
  const [email, setEmail] = useState(() => {
    const known = storage.getUser?.()?.email || '';
    return reasonKey ? (known || notice.email) : '';
  });
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [next, setNext] = useState('');
  // #139: with ?next= in the URL, first try the existing session; the form
  // shows only when that does not hand off (sso.js continueSession).
  const urlNext = new URLSearchParams(window.location.search).get('next') || '';
  const [checking, setChecking] = useState(Boolean(urlNext));
  const tried = useRef(false);

  // Stash ?next=&from= so we can carry them through the flow.
  useEffect(() => {
    const { next } = captureRoutingFromUrl();
    setNext(next);
    if (tried.current) return; // StrictMode runs effects twice in dev
    tried.current = true;
    continueSession({ next: urlNext, navigate }).then((done) => { if (!done) setChecking(false); });
  }, []);

  // A returning user with a known address goes straight to the password,
  // once the form is on screen (after the #139 session check).
  useEffect(() => {
    if (!checking && reasonKey && email) formRef.current?.elements?.password?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking]);

  // Validate only the field being left. Validating both on any blur
  // painted "Password is required" the moment the email lost focus and
  // swapped helper text in and out, which moved the Sign in button between
  // mousedown (the blur) and mouseup, so the FIRST click landed off the
  // button and did nothing. A field the user has not touched stays quiet.
  const validateField = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (field === 'email') {
        if (!email) delete next.email;
        else if (!/^\S+@\S+\.\S+$/.test(email)) next.email = t('login.emailInvalid');
        else delete next.email;
      } else if (password) {
        delete next.password;
      }
      return next;
    });
  };

  const formRef = useRef(null);

  const submit = async (ev) => {
    ev.preventDefault();
    // F001: when the browser autofills, React state may still be empty
    // even though the inputs hold valid values. Read the DOM directly
    // as a fallback before validating so the form submits cleanly.
    let effectiveEmail = email;
    let effectivePassword = password;
    if (formRef.current) {
      const fd = new FormData(formRef.current);
      const fe = String(fd.get('email') ?? '').trim();
      const fp = String(fd.get('password') ?? '');
      if (!effectiveEmail && fe) {
        effectiveEmail = fe;
        setEmail(fe);
      }
      if (!effectivePassword && fp) {
        effectivePassword = fp;
        setPassword(fp);
      }
    }
    // Validate against the post-autofill values.
    const e = {};
    if (!effectiveEmail) e.email = t('login.emailRequired');
    else if (!/^\S+@\S+\.\S+$/.test(effectiveEmail)) e.email = t('login.emailInvalid');
    if (!effectivePassword) e.password = t('login.passwordRequired');
    setErrors(e);
    if (Object.keys(e).length) return;
    setServerErr('');
    setLoading(true);
    try {
      const res = await api.login({ email: effectiveEmail.trim(), password: effectivePassword, nextUrl: next || undefined });
      storage.saveAuth({ token: res.token, refreshToken: res.refreshToken, user: res.user });
      handoffRedirect({
        token: res.token,
        refreshToken: res.refreshToken,
        redirectTo: res.redirectTo,
        navigate,
      });
    } catch (err) {
      // Do not reveal whether the email exists: the backend returns
      // INVALID_CREDENTIALS for both cases and errorMessage says the same
      // sentence for it. The message goes in the form-level alert.
      setServerErr(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // F001 (audit finding): the button used to disable when email and
  // password were both falsy in React state. Browser autofill writes
  // to the input element directly without firing onChange, so React
  // state stays empty while the inputs visually have values ,  making
  // the Sign in button look disabled even though the form is ready.
  // The button now disables only while a submit is in flight; the
  // existing validate() call at submit-time still rejects truly empty
  // values with inline field errors.
  const canSubmit = !loading;

  if (checking) {
    return (
      <div dir={dir} lang={lang} className="min-h-screen flex flex-col items-center justify-center bg-white">
        <VividWordmark />
        <p className="mt-6 text-[14px] text-stone-500" role="status">{t('login.signingIn')}</p>
      </div>
    );
  }

  return (
    <div dir={dir} lang={lang} className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <VividWordmark />
            <div className="flex items-center gap-4">
              <div className="text-[13px] text-stone-500 hidden sm:block">
                {t('login.newToVivid')}{' '}
                <Link to={`/signup${window.location.search}`} className="text-ink font-semibold hover:underline">
                  {t('login.createAccount')}
                </Link>
              </div>
              <LangSwitch lang={lang} setLang={setLang} />
            </div>
          </div>

          <div className="flex-1 flex items-start pt-[6vh] justify-center">
            <form ref={formRef} onSubmit={submit} className="w-full max-w-[420px]" noValidate>
              <h1 className="text-[28px] font-bold tracking-tight text-ink">{t('login.title')}</h1>
              <p className="mt-2 text-[15px] text-stone-500">{t('login.subtitle')}</p>
              {reasonKey && (
                <div role="status" className="banner banner-info mt-4">
                  {t(reasonKey)}
                  {returnLabel ? ` ${t('login.notice.returnTo', { label: returnLabel })}` : ''}
                </div>
              )}

              <div className="mt-8 space-y-2.5">
                <button type="button" disabled className="btn-secondary w-full" title="Coming soon">
                  <GoogleIcon /> {t('login.continueGoogle')}
                </button>
                <button type="button" disabled className="btn-secondary w-full" title="Coming soon">
                  <MsIcon /> {t('login.continueMicrosoft')}
                </button>
                <button type="button" disabled className="btn-secondary w-full" title="Coming soon">
                  <I.Key size={14} /> {t('login.continueSso')}
                </button>
              </div>

              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-[12px] text-stone-500">{t('login.orContinueWith')}</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>

              <div className="space-y-4">
                <TextField
                  label={t('common.email')}
                  name="email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  onBlur={() => validateField('email')}
                  placeholder={t('login.emailPlaceholder')}
                  error={errors.email}
                  helper={errors.email ? undefined : t('login.emailHelper')}
                  autoComplete="email"
                />
                <TextField
                  label={t('common.password')}
                  name="password"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={setPassword}
                  onBlur={() => validateField('password')}
                  placeholder="••••••••"
                  error={errors.password || undefined}
                  autoComplete="current-password"
                  rightAdornment={
                    <button type="button" className="p-1" onClick={() => setShowPwd((s) => !s)} aria-label={showPwd ? t('common.hidePassword') : t('common.showPassword')}>
                      {showPwd ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
                    </button>
                  }
                />
                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center gap-2 text-[13px] text-ink cursor-pointer">
                    <Checkbox checked={remember} onChange={setRemember} />
                    {t('login.rememberMe')}
                  </label>
                  <Link to="/forgot-password" className="text-[13px] font-medium text-ink hover:underline">
                    {t('invite.forgot')}
                  </Link>
                </div>
              </div>

              <div className="pt-8">
                <FormAlert message={serverErr} className="mb-4" />
                <button type="submit" disabled={!canSubmit} className="btn-primary w-full">
                  {loading ? (
                    <>
                      <svg role="status" aria-label="Loading" aria-busy="true" className="w-3.5 h-3.5 animate-spin" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" fill="none" /><path d="M 8 2 a 6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
                      {t('login.signingIn')}
                    </>
                  ) : (
                    t('common.signIn')
                  )}
                </button>
                <div className="text-center mt-4 text-[13px] text-stone-500 sm:hidden">
                  {t('login.doNotHaveAccount')}{' '}
                  <Link to={`/signup${window.location.search}`} className="text-ink font-semibold hover:underline">
                    {t('login.createAccount')}
                  </Link>
                </div>
              </div>
            </form>
          </div>

          <Footer t={t} />
        </div>

        <div className="hidden lg:block">
          <AuthDecor t={t} />
        </div>
      </div>
    </div>
  );
}
